import Add from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiKey } from '../../contexts/ApiKeyContext';
import { useSelectedTournament } from '../../contexts/SelectedTournamentContext';
import useTenant from '../../hooks/useTenant';
import { Tournament, TStateEnum } from '../../models/Tournament';
import { deleteTournament, fetchAllTournaments } from '../../services/TournamentService';
import { ConfirmDialog } from '../../utils/ConfirmDialog';
import { AddTournamentDialog } from '../adders/AddTournamentDialog';
import { IndividualTrophiesDialog } from '../editors/IndiviudalTrophiesDialog';
import { TournamentRow } from './TournamentRow';
import { EnrollmentCards } from '../enrollment/EnrollmentCards';

export const TournamentViewer: React.FC = () => {
  const { tId } = useTenant();
  const { apiKey, isAdmin, userProfile } = useApiKey();
  const { setSelectedTournament, clearSelectedTournament } = useSelectedTournament();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [tournamentToRemove, setTournamentToRemove] = useState<Tournament>();
  const [futureCollapsed, setFutureCollapsed] = useState(true);
  const [currentCollapsed, setCurrentCollapsed] = useState(false);
  const [pastCollapsed, setPastCollapsed] = useState(true);
  const [addTournament, setAddTournament] = useState<boolean>(false);
  const [modifyTrophiesTournament, setModifyTrophiesTournament] = useState<Tournament>();

  const { future, current, past } = useMemo(() => {
    const now = dayjs();
    const future: Tournament[] = [];
    const current: Tournament[] = [];
    const past: Tournament[] = [];

    const weekStart = now.startOf('week');
    const weekEnd = now.endOf('week');

    tournaments.forEach((t) => {
      if (t.startDate !== undefined && t.startDate !== null) {
        const start = dayjs(t.startDate);
        if (start.isBefore(weekStart)) {
          past.push(t);
        } else if (start.isAfter(weekEnd)) {
          future.push(t);
        } else {
          current.push(t);
        }
      } else {
        past.push(t);
      }
    });

    return { future, current, past };
  }, [tournaments]);

  const enrollmentCards = useMemo(() => {
    const cards: Tournament[] = [];
    cards.push(...future.filter((t) => TStateEnum.RegistrationOpen === t.state));
    cards.push(...current.filter((t) => TStateEnum.RegistrationOpen === t.state));
    return cards;
  }, [future, current]);

  const getTournaments = () => {
    fetchAllTournaments(apiKey, tId)
      .then((result) => {
        setTournaments(
          result.sort((a, b) => {
            const aDate = a.startDate ? dayjs(a.startDate) : null;
            const bDate = b.startDate ? dayjs(b.startDate) : null;

            if (aDate && bDate) {
              return aDate.isAfter(bDate) ? -1 : 1;
            }

            if (aDate && !bDate) return -1;
            if (!aDate && bDate) return 1;

            return b.id - a.id;
          }),
        );
      })
      .catch((error: Response) => {
        console.error('Failed to fetch tournaments', error);
        if (error.status === 401) {
          navigate('/logout');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    clearSelectedTournament();
  }, [clearSelectedTournament]);

  useEffect(() => {
    getTournaments();
  }, [apiKey]);

  const handleAdd = () => {
    setAddTournament(true);
  };

  const handleEdit = (id: number) => {
    navigate(`/edit-tournament/${id}`);
  };

  const goToSchedule = (t: Tournament) => {
    setSelectedTournament(t);
    navigate(`/tournament/${t.id}/schedule`);
  };

  const goToResults = (t: Tournament) => {
    setSelectedTournament(t);
    if (t.id !== 121) {
      navigate(`/tournament/${t.id}/result`);
    } else {
      goToSchedule(t);
    }
  };

  const handleTrophiesEdit = (t: Tournament) => {
    setModifyTrophiesTournament(t);
  };

  const goToEnrollment = (t: Tournament) => {
    //setSelectedTournament(t);
    navigate(`/tournament/${t.id}/enroll`);
  };

  const handleDelete = () => {
    if (tournamentToRemove) {
      setLoading(true);
      deleteTournament(apiKey, tournamentToRemove.id)
        .then(getTournaments)
        .catch((error) => {
          console.error('Error on tournament delete: ', error);
        })
        .finally(() => {
          setTournamentToRemove(undefined);
          setLoading(false);
        });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const singleCellColSpan = isAdmin ? 4 : 2;

  const getCollapsibleSection = (
    title: string,
    tournamentsArr: Tournament[],
    collapsed: boolean,
    setCollapsed: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (tournamentsArr.length === 0) return null;

    return (
      <>
        {/* Header Row with Toggle Button */}
        <TableRow>
          <TableCell
            colSpan={singleCellColSpan}
            align="center"
            style={{ borderBottom: 'none', cursor: 'pointer' }}
            className="row-separator"
            onClick={() => setCollapsed(!collapsed)}
          >
            <Box display="flex" alignItems="center" justifyContent="center">
              <IconButton aria-label="toggle collapse">
                {collapsed ? <KeyboardArrowRightIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
              <Typography variant="h6" component="span" sx={{ ml: 1 }}>
                {title} ({tournamentsArr.length})
              </Typography>
            </Box>
          </TableCell>
        </TableRow>

        {/* Collapsible Content */}
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={singleCellColSpan}>
            <Collapse in={!collapsed} timeout="auto" unmountOnExit>
              <Table size="small">
                <TableBody>
                  {tournamentsArr.map((t) => (
                    <TournamentRow
                      key={t.id}
                      tournament={t}
                      role={userProfile?.role}
                      onEdit={handleEdit}
                      onDelete={setTournamentToRemove}
                      onSchedule={goToSchedule}
                      onResults={goToResults}
                      onTrophies={handleTrophiesEdit}
                      onEnrollment={goToEnrollment}
                    />
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  const getTableRows = () => {
    if (tournaments.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={singleCellColSpan} align="center">
            <Typography variant="body1">Brak turniejów</Typography>
          </TableCell>
        </TableRow>
      );
    } else {
      return (
        <>
          {getCollapsibleSection('Przyszłe', future, futureCollapsed, setFutureCollapsed)}
          {getCollapsibleSection('Bieżące', current, currentCollapsed, setCurrentCollapsed)}
          {getCollapsibleSection('Rozegrane', past, pastCollapsed, setPastCollapsed)}
        </>
      );
    }
  };

  return (
    <>
      <EnrollmentCards tournaments={enrollmentCards} />
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nazwa</TableCell>
              <TableCell align="right">
                Akcje
                {isAdmin && (
                  <Tooltip arrow title="Stwórz nowy turniej" placement="left">
                    <IconButton onClick={handleAdd} color="primary">
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{getTableRows()}</TableBody>
        </Table>
      </TableContainer>
      {tournamentToRemove && (
        <ConfirmDialog
          title={`Czy jesteś pewien, że chcesz usunąć turniej ${tournamentToRemove.name}?`}
          content={`Usunięcie jest operacją nieodwracalną. ${tournamentToRemove.state > TStateEnum.Draft ? 'Turniej ten jest publiczny.' : ''}`}
          onClose={() => setTournamentToRemove(undefined)}
          onConfirm={handleDelete}
        />
      )}
      {addTournament && <AddTournamentDialog tenantId={tId} onClose={() => setAddTournament(false)} />}
      {modifyTrophiesTournament && (
        <IndividualTrophiesDialog
          tournament={modifyTrophiesTournament}
          onClose={() => setModifyTrophiesTournament(undefined)}
          onSave={function (): void {
            throw new Error('Function not implemented.');
          }}
        />
      )}
    </>
  );
};
